import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Measurement : famille "valeur chiffree horodatee".
 *
 * Valeur numerique constatee sur une entite a un instant donne : poids,
 * temperature, kilometrage, releve de compteur, niveau. Construite sur le
 * modele "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connait pas la grandeur metier concrete ("pesee du chat",
 * "releve kilometrique") : il connait la famille generique `measurement`. Le
 * vocabulaire de sous-type est porte par le champ `type` (chaine libre declaree
 * par l'app) ; la grandeur mesuree est nommee par `metric` (ex. "weight",
 * "temperature", "mileage"), le nombre par `value` et son unite par `unit`.
 *
 * Cible (targetRef) : polymorphe, un seul des trois ids est en general
 * renseigne selon le sujet mesure. `assetId` pour un bien (equipement, animal),
 * `placeId` pour un lieu, `careRecordId` pour un sujet suivi cote sante (le
 * "careSubjectRef" de la fiche : mesurer un humain passe par le suivi de soin,
 * dont la mesure herite le niveau de visibilite). Tous optionnels.
 *
 * References vers d'autres familles = de simples ids (aucun import de schema) :
 * la mesure peut avoir ete relevee pendant une `inspection`, refacturee sur une
 * `invoice`, ou constatee au cours d'un `workOrder`. Toutes optionnelles.
 *
 * Securite / acces : la visibilite HERITE toujours de celle du `targetRef`. Une
 * mesure sur un humain releve de la donnee de sante (RGPD art. 9) via le
 * care-record ; sur un equipement ou un lieu, elle est de niveau org. La valeur
 * `value` est le coeur de la famille (comme `amount` pour payment) : elle ne
 * peut pas etre extraite dans un sous-document. En v0 la famille reste org-wide
 * (meme doctrine qu'asset et que careRecord) ; le cloisonnement fin (niveau
 * sante quand la cible est un sujet de soin) viendra apres, hors de ce schema.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs METIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complete = champs standard + champs metier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit a la creation, VALIDE
 *     AVANT toute ecriture Firestore.
 */

/** Champs metier de la famille measurement (hors champs standard du socle). */
export const measurementCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type declare par l'app (ex. "vitals", "odometer", "reading"). */
  type: z.string().min(1),
  /** Cible : bien mesure (reference `asset`), optionnel (un des trois targetRef). */
  assetId: z.string().min(1).optional(),
  /** Cible : lieu mesure (reference `place`), optionnel (un des trois targetRef). */
  placeId: z.string().min(1).optional(),
  /** Cible : sujet de soin mesure (reference `care-record`, le careSubjectRef de la fiche), optionnel. */
  careRecordId: z.string().min(1).optional(),
  /** Grandeur mesuree (ex. "weight", "temperature", "mileage"). Coeur de la mesure. */
  metric: z.string().min(1),
  /** Nombre releve (decimal autorise : 4.2 kg, 37.5 degres). Coeur de la mesure. */
  value: z.number(),
  /** Unite du nombre (ex. "kg", "degC", "km"), optionnel. */
  unit: z.string().min(1).optional(),
  /** Instant du releve (horodatage metier, distinct de createdAt), optionnel. */
  measuredAt: z.date().optional(),
  /** Intervenant qui a releve la mesure (reference `worker`), optionnel. */
  measuredByWorkerId: z.string().min(1).optional(),
  /** Inspection au cours de laquelle la mesure a ete relevee (reference `inspection`), optionnel. */
  inspectionId: z.string().min(1).optional(),
  /** Facture qui refacture la mesure (reference `invoice`), optionnel. */
  invoiceId: z.string().min(1).optional(),
  /** Ordre de travail au cours duquel la mesure a ete constatee (reference `work-order`), optionnel. */
  workOrderId: z.string().min(1).optional(),
} as const;

/** Schema complet de la famille measurement (champs standard + champs metier). */
export const MeasurementSchema = z.object({
  ...baseEntityFields,
  ...measurementCoreFields,
});

/** Type d'une measurement. */
export type Measurement = z.infer<typeof MeasurementSchema>;

/**
 * Schema des donnees fournies par l'appelant a la creation.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const MeasurementCreateInputSchema = z.object({
  ...measurementCoreFields,
  ...customFieldsField,
});

/** Type de l'entree de creation d'une measurement. */
export type MeasurementCreateInput = z.infer<typeof MeasurementCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const MeasurementUpdateInputSchema = z
  .object({ ...measurementCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une measurement. */
export type MeasurementUpdateInput = z.infer<typeof MeasurementUpdateInputSchema>;
